import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { getSession } from 'next-auth/react';
import React, { useState } from 'react';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';

const MOBILE = '(max-width: 992px)';

const BarChartIcon = ({ size = 28 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="12" width="4" height="9" rx="1" fill="currentColor" opacity="0.5" />
    <rect x="10" y="7" width="4" height="14" rx="1" fill="currentColor" opacity="0.7" />
    <rect x="17" y="3" width="4" height="18" rx="1" fill="currentColor" />
  </svg>
);

interface Props {
  isAuthenticated: boolean;
}

const TOPICS = [
  'All',
  'Writing',
  'ELA',
  'Science',
  'Social Studies',
  'Test Prep',
  'STAAR',
  'CAASPP',
  'Engagement',
  'AI Feedback',
  'Bilingual',
  'Disadvantaged Students',
  'Special Education',
  'Dyslexia',
  'Small & Rural',
  'Higher Ed',
  'Virtual',
  'Independent Research',
] as const;

type Topic = typeof TOPICS[number];

const caseStudyData = [
  {
    title: 'From 69% to 89%',
    subtitle: "Green Valley's Third-Grade Growth Story",
    location: 'Schertz-Cibolo-Universal City ISD, TX',
    grade: '3rd Grade RLA',
    year: '2024\u20132025',
    stat: '+20 pts',
    statLabel: 'STAAR RLA Passing',
    topics: ['Test Prep', 'STAAR', 'Writing', 'AI Feedback', 'ELA'] as Topic[],
    quote:
      'It\u2019s not just about numbers. Our kids believe they can do hard things now.',
    quoteAttrib: 'Kimberly McDaniel, 3rd Grade Teacher',
    link: '/case-studies/curipod-case-study-green-valley',
  },
  {
    title: 'From 46% to 84%',
    subtitle: 'Real-Time Feedback, Real Results',
    location: 'South Texas',
    grade: 'Grades 6\u20138 RLA',
    year: '2022\u20132025',
    stat: '+38 pts',
    statLabel: '6th Grade Meets & Masters',
    topics: [
      'Writing',
      'AI Feedback',
      'Engagement',
      'ELA',
      'STAAR',
      'Disadvantaged Students',
    ] as Topic[],
    quote:
      'I saw students eager and excited to learn! And actually wanting to write\u2026 Just look at the scores.',
    quoteAttrib: 'Principal',
    link: '/case-studies/curipod-case-study-south-east-texas',
  },
  {
    title: 'From C to A in One Year',
    subtitle: "Sivells Bend's Unprecedented Gains",
    location: 'Sivells Bend ISD, TX',
    grade: '8th Grade ELAR & Social Studies',
    year: '2024\u20132025',
    stat: '100%',
    statLabel: 'Meets & Masters on STAAR',
    topics: [
      'Test Prep',
      'STAAR',
      'Small & Rural',
      'Engagement',
      'ELA',
      'Social Studies',
    ] as Topic[],
    quote: 'Miss, Curipod gave me a five. I can pass the STAAR!',
    quoteAttrib: 'Student, Sivells Bend ISD',
    link: '/case-studies/curipod-case-study-sivells-bend',
  },
  {
    title: '22% Increase in Achievement',
    subtitle: 'How One Teacher Transformed 6th-Grade Writing',
    location: 'South Texas',
    grade: '6th Grade RLA',
    year: '2023\u20132024',
    stat: '+22%',
    statLabel: 'Meets & Masters',
    topics: ['Writing', 'AI Feedback', 'Test Prep', 'STAAR', 'ELA'] as Topic[],
    quote: 'They\u2019re closing the gap! How did they do that?',
    quoteAttrib: 'Ms. G\u2019s Principal',
    link: '/case-studies/curipod-case-study-south-texas',
  },
  {
    title: 'Immediate Feedback, Immediate Results',
    subtitle: 'An Ysleta Middle School Impact Story',
    location: 'Ysleta ISD, TX',
    grade: '6th & 8th Grade RLA',
    year: 'Emergent Bilingual',
    stat: 'chart',
    statLabel: 'Igniting Writing Growth',
    topics: [
      'Bilingual',
      'AI Feedback',
      'Writing',
      'ELA',
      'STAAR',
      'Disadvantaged Students',
    ] as Topic[],
    quote:
      'Curipod is the most innovative tool I\u2019ve used in my classroom in the last five years!',
    quoteAttrib: 'Melissa Castillo, 6th Grade Teacher',
    link: '/case-studies/curipod-case-study-ysleta-bilingual',
  },
  {
    title: 'From 6% to 49%',
    subtitle: "Flour Bluff's STAAR Growth Story",
    location: 'Flour Bluff ISD, TX',
    grade: '4th Grade ELAR',
    year: '2024\u20132025',
    stat: '+43 pts',
    statLabel: 'Students in Top 40% STAAR',
    topics: [
      'Test Prep',
      'STAAR',
      'AI Feedback',
      'Small & Rural',
      'ELA',
      'Special Education',
      'Dyslexia',
    ] as Topic[],
    quote:
      'Everybody screamed when they got a one\u2026 it\u2019s the most tangible takeaway.',
    quoteAttrib: 'Nadine Lynch, Curriculum Supervisor',
    link: '/case-studies/curipod-case-study-flour-bluff',
  },
  {
    title: 'Five Steps, Real Results',
    subtitle: 'Charlie Marshall Elementary Impact Story',
    location: 'Aransas Pass ISD, TX',
    grade: '3rd & 4th Grade ELAR',
    year: '2025\u20132026',
    stat: '+43%',
    statLabel: 'ECR Growth (3rd Grade)',
    topics: ['Writing', 'Test Prep', 'STAAR', 'ELA'] as Topic[],
    quote: 'When strong leadership meets strong instruction, real change follows.',
    quoteAttrib: 'Charlie Marshall Elementary',
    link: '/case-studies/curipod-case-study-charlie-marshall',
  },
  {
    title: 'From 51% to 69%',
    subtitle: 'Transforming Writing Instruction in Southern CA',
    location: 'Southern California',
    grade: '5th Grade ELA',
    year: '2024\u20132025',
    stat: '+35%',
    statLabel: 'Growth in ELA',
    topics: [
      'Writing',
      'AI Feedback',
      'Engagement',
      'ELA',
      'CAASPP',
      'Disadvantaged Students',
    ] as Topic[],
    quote: 'It\u2019s the most revolutionary tool in my teaching career.',
    quoteAttrib: 'Ms. M., 5th Grade Teacher',
    link: '/case-studies/curipod-case-study-southern-ca',
  },
  {
    title: '46% CAASPP Growth',
    subtitle: 'Targeted Writing Practice & Immediate Feedback',
    location: 'Southern California',
    grade: 'Upper Elementary ELA',
    year: '2023\u20132024',
    stat: '+46%',
    statLabel: 'CAASPP Growth',
    topics: ['Test Prep', 'CAASPP', 'Writing', 'AI Feedback', 'ELA'] as Topic[],
    quote: 'They also attribute the growth to using Curipod.',
    quoteAttrib: 'Tech Coach',
    link: '/case-studies/california-writing-achievement',
  },
  {
    title: 'Double-Digit Growth',
    subtitle: 'Innovations Academy \u2014 Virtual',
    location: 'Palmdale School District, CA',
    grade: 'K-8 Virtual School',
    year: '2024\u20132025',
    stat: '+19%',
    statLabel: '7th Grade ELA Growth',
    topics: [
      'Virtual',
      'Engagement',
      'Test Prep',
      'CAASPP',
      'Disadvantaged Students',
    ] as Topic[],
    quote: 'It is a game changer. Kids ask, \u2018Can we do a Curipod?\u2019',
    quoteAttrib: 'Megann Kane, Co-Teacher',
    link: '/case-studies/curipod-case-study-innovations-academy',
  },
  {
    title: 'Instructional Shifts That Stick',
    subtitle: 'A Northville Impact Story',
    location: 'Northville Public Schools, MI',
    grade: 'Middle School ELA & Social Studies',
    year: 'Meads Mill Middle School',
    stat: 'chart',
    statLabel: 'Engagement & Confidence',
    topics: ['Engagement', 'Writing', 'ELA', 'Social Studies'] as Topic[],
    quote: 'Curipod doesn\u2019t just help you teach better. It makes you feel better.',
    quoteAttrib: 'Amy Soukup, ELA & Social Studies Teacher',
    link: '/case-studies/curipod-case-study-michigan',
  },
  {
    title: 'From Lecture Hall to Learning Community',
    subtitle: 'Inclusive Engagement in Higher Education',
    location: 'Butler University, IN',
    grade: 'Undergraduate Biology',
    year: '~50 Students per Lecture',
    stat: 'chart',
    statLabel: 'Mood, Engagement & Inclusion',
    topics: ['Higher Ed', 'Engagement', 'Science'] as Topic[],
    quote: 'It\u2019s really changed how I teach this past year.',
    quoteAttrib: 'Ashlee Tietje, Undergraduate Biology Lecturer',
    link: '/case-studies/curipod-case-study-butler-university',
  },
];

const researchStudies = [
  {
    title: "AI-Powered Pedagogy Enhances Nursing Students' Competencies",
    description:
      'Peer-reviewed research showing how AI-powered interactive pedagogy significantly enhances knowledge acquisition, clinical reasoning, and learning engagement.',
    link: '/research-studies/ai-powered-pedagogy-nursing',
    tags: ['Higher Education', 'AI Pedagogy'],
    topics: ['Higher Ed', 'AI Feedback', 'Independent Research'] as Topic[],
    icon: '🏥',
  },
  {
    title: 'AI Tools Improve Vocabulary and Engagement in Oman',
    description:
      'Research demonstrating how AI-based tools improve vocabulary acquisition and boost student engagement in EFL classrooms.',
    link: '/research-studies/vocabulary-engagement-oman',
    tags: ['EFL', 'Vocabulary'],
    topics: ['Bilingual', 'Engagement', 'Independent Research'] as Topic[],
    icon: '🌍',
  },
  {
    title: 'Curipod Upskills New Teachers and Reduces Planning Time by 30%',
    description:
      'Independent research showing how Curipod helps new teachers build confidence while cutting planning time by nearly a third.',
    link: '/research-studies/upskills-new-teachers',
    tags: ['Teacher Development', 'Planning'],
    topics: ['Engagement', 'Independent Research'] as Topic[],
    icon: '📊',
  },
  {
    title: 'Cooperative Learning With Curipod Drives Student Success',
    description:
      'Research demonstrating how Curipod fosters cooperative learning environments that lead to measurable student outcome improvements.',
    link: '/research-studies/cooperative-learning-boosts-success',
    tags: ['Cooperative Learning', 'Engagement'],
    topics: ['Engagement', 'Independent Research'] as Topic[],
    icon: '🤝',
  },
  {
    title: 'Increasing Science Class Engagement with Curipod',
    description:
      'Research exploring how Curipod transforms science classrooms by boosting participation and supporting inquiry-based learning.',
    link: '/research-studies/science-class-engagement',
    tags: ['Science', 'Engagement'],
    topics: ['Science', 'Engagement', 'Independent Research'] as Topic[],
    icon: '🔬',
  },
];

const CaseStudiesPage = ({ isAuthenticated }: Props) => {
  const [activeTopic, setActiveTopic] = useState<Topic>('All');

  const filteredCaseStudies =
    activeTopic === 'All'
      ? caseStudyData
      : caseStudyData.filter(s => s.topics.includes(activeTopic));

  const filteredResearch =
    activeTopic === 'All'
      ? researchStudies
      : researchStudies.filter(s => s.topics.includes(activeTopic));

  const hasResults = filteredCaseStudies.length > 0 || filteredResearch.length > 0;

  return (
    <LandingPageLayout
      title="Case Studies & Research | Curipod"
      subTitle="Real stories of measurable impact and independent research validating Curipod."
      disableHeadline={true}
    >
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>IMPACT &amp; EFFICACY</HeroTag>
            <HeroTitle>Case Studies &amp; Research</HeroTitle>
            <HeroSubtitle>
              Real stories of measurable impact from K-12 and higher education
            </HeroSubtitle>
            <HeroDescription>
              Explore how schools and districts across the country are using Curipod to
              drive student achievement, engagement, and growth — backed by documented
              results and independent research.
            </HeroDescription>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <GridSection>
        <MaxWidthContainer>
          <FilterBar>
            {TOPICS.map(topic => (
              <FilterPill
                key={topic}
                $active={activeTopic === topic}
                onClick={() => setActiveTopic(topic)}
              >
                {topic}
              </FilterPill>
            ))}
          </FilterBar>

          {hasResults ? (
            <CaseStudyGrid>
              {filteredCaseStudies.map(study => (
                <Link key={study.link} href={study.link} passHref>
                  <CaseStudyCard as="a">
                    <CardStatBadge>
                      <CardStatNumber>
                        {study.stat === 'chart' ? <BarChartIcon /> : study.stat}
                      </CardStatNumber>
                      <CardStatLabel>{study.statLabel}</CardStatLabel>
                    </CardStatBadge>
                    <CardTitle>{study.title}</CardTitle>
                    <CardSubtitle>{study.subtitle}</CardSubtitle>
                    <CardQuote>
                      &ldquo;{study.quote}&rdquo;
                      <CardQuoteAttrib>&mdash; {study.quoteAttrib}</CardQuoteAttrib>
                    </CardQuote>
                    <CardMeta>
                      <MetaBadge>{study.location}</MetaBadge>
                      <MetaBadge>{study.grade}</MetaBadge>
                      <MetaBadge>{study.year}</MetaBadge>
                    </CardMeta>
                    <ReadMore>Read case study &rarr;</ReadMore>
                  </CaseStudyCard>
                </Link>
              ))}

              {filteredResearch.map(study => (
                <ResearchCard key={study.link}>
                  <ResearchStatBadge>
                    <ResearchIconInBadge>{study.icon}</ResearchIconInBadge>
                    <ResearchStatBadgeTitle>{study.title}</ResearchStatBadgeTitle>
                  </ResearchStatBadge>
                  <IndependentTag>Independent Research</IndependentTag>
                  <CardSubtitle>{study.description}</CardSubtitle>
                  <CardMeta>
                    {study.tags.map(tag => (
                      <ResearchBadge key={tag}>{tag}</ResearchBadge>
                    ))}
                  </CardMeta>
                  <Link href={study.link} passHref>
                    <a>
                      <ResearchReadMore>Read the research &rarr;</ResearchReadMore>
                    </a>
                  </Link>
                </ResearchCard>
              ))}
            </CaseStudyGrid>
          ) : (
            <NoResults>No results match this topic.</NoResults>
          )}
        </MaxWidthContainer>
      </GridSection>

      <CtaSection>
        <MaxWidthContainer>
          <CenteredSection>
            <CtaSectionTag>SEE THE IMPACT FOR YOURSELF</CtaSectionTag>
            <CtaTitle>Ready to Transform Your Classroom?</CtaTitle>
            <CtaDescription>
              Join thousands of educators who are driving measurable student growth with
              Curipod. Start for free today.
            </CtaDescription>
            <Link href={isAuthenticated ? '/' : '/signup'} passHref>
              <a>
                <Button
                  type="orange"
                  styles={{
                    borderRadius: '20px',
                    padding: '0 36px',
                    fontSize: 18,
                    height: 52,
                  }}
                >
                  Get started for free
                </Button>
              </a>
            </Link>
          </CenteredSection>
        </MaxWidthContainer>
      </CtaSection>
    </LandingPageLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const session = await getSession({ req: context.req });
  return {
    props: {
      isAuthenticated: !!session,
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'marketing' })),
    },
  };
};

export default CaseStudiesPage;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(135deg, #d97a2a 0%, #c06a1e 100%);
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 80px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 60px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.25);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
`;

const HeroTitle = styled.h1`
  font-size: 56px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

  @media ${MOBILE} {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 24px;
  color: ${theme.colors.white};
  font-weight: 700;
  margin: 0 0 20px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  @media ${MOBILE} {
    font-size: 20px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  max-width: 700px;
  line-height: 1.7;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

/* ─── GRID SECTION ─────────────────────────────────────────────────────────── */

const GridSection = styled.section`
  background-color: ${theme.colors.background};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    gap: 6px;
  }
`;

const FilterPill = styled.button<{ $active: boolean }>`
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.effects.defaultTransition};
  border: 2px solid
    ${({ $active }) => ($active ? theme.colors.orange : theme.colors.beigeDark)};
  background: ${({ $active }) => ($active ? theme.colors.orange : theme.colors.white)};
  color: ${({ $active }) => ($active ? theme.colors.white : theme.colors.black)};
  white-space: nowrap;

  &:hover {
    border-color: ${theme.colors.orange};
    background: ${({ $active }) =>
      $active ? theme.colors.orangeDark : 'rgba(249, 151, 70, 0.08)'};
  }
`;

const NoResults = styled.p`
  text-align: center;
  font-size: 18px;
  color: ${theme.colors.text.dark};
  padding: 60px 20px;
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const CaseStudyCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-4px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const CardStatBadge = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
`;

const CardStatNumber = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${theme.colors.white};
  line-height: 1;
  margin-bottom: 4px;
`;

const CardStatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.5px;
`;

const CardTitle = styled.h3`
  font-size: 22px;
  color: ${theme.colors.black};
  margin-bottom: 8px;
  line-height: 1.3;
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.5;
  margin-bottom: 12px;
`;

const CardQuote = styled.blockquote`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.55;
  margin: 0 0 16px;
  padding: 10px 14px;
  background: rgba(249, 151, 70, 0.06);
  border-left: 3px solid ${theme.colors.orange};
  border-radius: 0 8px 8px 0;
  flex: 1;
`;

const CardQuoteAttrib = styled.cite`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  margin-top: 6px;
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
`;

const MetaBadge = styled.span`
  background: rgba(249, 151, 70, 0.1);
  color: ${theme.colors.orangeDark};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid rgba(249, 151, 70, 0.25);
`;

const ReadMore = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.orange};
  letter-spacing: 0.3px;
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── RESEARCH CARDS ──────────────────────────────────────────────────────── */

const ResearchCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-top: 4px solid ${theme.colors.green};
  transition: ${theme.effects.defaultTransition};
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-4px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ResearchStatBadge = styled.div`
  background: linear-gradient(135deg, ${theme.colors.green} 0%, #0f7a68 100%);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
`;

const ResearchIconInBadge = styled.div`
  font-size: 28px;
  line-height: 1;
  margin-bottom: 4px;
`;

const ResearchStatBadgeTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${theme.colors.white};
  line-height: 1.3;
`;

const IndependentTag = styled.span`
  display: inline-block;
  background: rgba(19, 153, 130, 0.1);
  color: ${theme.colors.green};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 10px;
  border: 1px solid rgba(19, 153, 130, 0.25);
  margin-bottom: 12px;
  align-self: flex-start;
`;

const ResearchBadge = styled.span`
  background: rgba(19, 153, 130, 0.1);
  color: ${theme.colors.green};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid rgba(19, 153, 130, 0.25);
`;

const ResearchReadMore = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${theme.colors.green};
  letter-spacing: 0.3px;
`;

/* ─── CTA ─────────────────────────────────────────────────────────────────── */

const CtaSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const CtaSectionTag = styled.span`
  display: block;
  text-align: center;
  color: ${theme.colors.orange};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const CtaTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.white};
  text-align: center;
  margin-bottom: 16px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const CtaDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  max-width: 560px;
  line-height: 1.7;
  margin: 0 auto 40px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;
